import { TableCell, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useEffect, useState } from "react";
import * as React from "react";
import { HqType } from "../../../../../domain/models/daily-visit-report/dailyVisitReport";
import VisitDataModal from "../Modal/VisitDataModal";
import LeaveDataModal from "../Modal/LeaveDataModal";

type Props = {
  open: boolean;
  hq: HqType;
  month: number;
  year: number;
  user_id: number;
  totalRegion: number[] | string[];
  totalArea: number[] | string[];
  downLoadJson: any[];
  customerTypeId: string | null;
};

const CalendarGridDailyVisitData: React.FC<Props> = ({
  open,
  hq,
  month,
  year,
  user_id,
  totalRegion,
  totalArea,
  downLoadJson,
  customerTypeId,
}) => {
  const monthlyDataJSON: any = {};
  const getDayOfWeek = (day: number) => {
    return new Date(year, month - 1, day).getDay();
  };
  const getDaysInMonth = () => {
    return new Date(year, month, 0).getDate();
  };
  const [total, setTotal] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openLeaveModal, setOpenLeaveModal] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [day, setDay] = useState<number>(0);
  const [userId, setUserId] = useState<number>(0);
  const [leave, setLeave] = useState<string>("");
  const openDataModal = (day: number, user_id: number, name: string) => {
    setOpenModal((prevOpen) => !prevOpen);
    setDay(day);
    setName(name);
    setUserId(user_id);
  };
  const leaveModal = (
    day: number,
    user_id: number,
    name: string,
    leaveType: string
  ) => {
    setOpenLeaveModal((open) => !open);
    setName(name);
    setDay(day);
    setUserId(user_id);
    setLeave(leaveType);
  };

  useEffect(() => {
    let totalValue = 0;
    for (let day = 1; day <= getDaysInMonth(); day++) {
      const dataForDay = hq.monthly_data.find((item) => item.day === day);
      if (dataForDay && dataForDay.total_visits) {
        totalValue += dataForDay.total_visits;
      }
    }
    setTotal(totalValue);
    monthlyDataJSON.total = totalValue;
  }, [hq, year, month]);

  for (let day = 1; day <= getDaysInMonth(); day++) {
    const dataForDay = hq.monthly_data.find((item) => item.day === day);
    const isSunday = getDayOfWeek(day) === 0;
    const dayPropertyName = `${day}`;
    monthlyDataJSON.role = "TSO";
    monthlyDataJSON.user = hq.tso_name;
    monthlyDataJSON.territory = hq.hq_name;

    if (isSunday || (dataForDay && dataForDay.att_status === "holiday")) {
      totalRegion[day - 1] = isSunday ? "H" : totalRegion[day - 1];
      totalArea[day - 1] = isSunday ? "H" : totalArea[day - 1];
      monthlyDataJSON[dayPropertyName] = "H";
    } else if (
      dataForDay &&
      dataForDay.att_status === "working" &&
      dataForDay.total_visits
    ) {
      monthlyDataJSON[dayPropertyName] = dataForDay.total_visits;
      totalRegion[day - 1] =
        (totalRegion[day - 1] as number) + dataForDay.total_visits;
      totalArea[day - 1] =
        (totalArea[day - 1] as number) + dataForDay.total_visits;
    } else if (dataForDay && dataForDay.att_status === "leave") {
      monthlyDataJSON[dayPropertyName] =
        dataForDay.att_type !== null
          ? dataForDay.att_type
              .split(" ")
              .map((word) => word[0])
              .join("")
          : "-";
    } else {
      monthlyDataJSON[dayPropertyName] = "-";
    }
  }

  useEffect(() => {
    const existingIndex = downLoadJson.findIndex(
      (item) => item.user === monthlyDataJSON.user
    );
    if (existingIndex !== -1) {
      downLoadJson[existingIndex] = monthlyDataJSON;
    } else {
      downLoadJson.push(monthlyDataJSON);
    }
  }, [month, year]);

  const rowData = () => {
    const rows: JSX.Element[] = [];

    for (let day = 1; day <= getDaysInMonth(); day++) {
      const dataForDay = hq.monthly_data.find((item) => item.day === day);
      const isSunday = getDayOfWeek(day) === 0;

      if (dataForDay && dataForDay.att_status === "working") {
        rows.push(
          <Tooltip
            arrow
            title={
              <React.Fragment>
                <Typography sx={{ color: "#000080" }}>
                  {`Total Customer = ${dataForDay?.total_visits ?? 0}`}
                </Typography>
                {dataForDay.customer_count.map((value) => (
                  <Typography
                    key={value.customer_type_name}
                    sx={{ color: "#000080" }}
                  >
                    {value.customer_type_name} = {value.count}
                  </Typography>
                ))}
              </React.Fragment>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#FFFFFF",
                  color: "#000080",
                  boxShadow: 3,
                  border: "1px solid #FF9933",
                  padding: "8px",
                },
              },
              arrow: {
                sx: {
                  color: "#FFFFFF",
                },
              },
            }}
          >
            <TableCell
              align="center"
              style={{
                border: "2px solid white",
                borderRadius: "2px",
                padding: 0,
                color: "white",
                cursor: isSunday ? "default" : "pointer",
                fontWeight: 550,
                backgroundColor: isSunday
                  ? "#d5dbe5"
                  : dataForDay.total_visits
                  ? dataForDay.total_visits > 20
                    ? "#13c46f"
                    : dataForDay.total_visits > 10
                    ? "#f1c40f"
                    : "#d1293d"
                  : "#d1293d",
                fontSize: 10,
              }}
              onClick={() => {
                if (!isSunday) openDataModal(day, user_id, hq.tso_name);
              }}
            >
              {dataForDay.total_visits ?? "0"}
            </TableCell>
          </Tooltip>
        );
      } else if (dataForDay && dataForDay.att_status === "leave") {
        rows.push(
          <TableCell
            align="center"
            style={{
              border: "2px solid white",
              borderRadius: "2px",
              padding: 0,
              color: "white",
              cursor: isSunday ? "default" : "pointer",
              fontWeight: 550,
              backgroundColor: isSunday ? "#d5dbe5" : "#d1293d",
              fontSize: 10,
            }}
            onClick={() => {
              if (!isSunday && dataForDay.att_type !== null)
                leaveModal(day, user_id, hq.tso_name, dataForDay.att_type);
            }}
          >
            {dataForDay.att_type !== null &&
              dataForDay.att_type
                .split(" ")
                .map((word) => word[0])
                .join("")}
          </TableCell>
        );
      } else {
        rows.push(
          <TableCell
            align="center"
            style={{
              border: "2px solid white",
              padding: 0,
              cursor: "default",
              backgroundColor:
                isSunday || (dataForDay && dataForDay.att_status === "holiday")
                  ? "#d5dbe5"
                  : "#edf6fc",
              fontSize: 10,
            }}
          >
            {isSunday || (dataForDay && dataForDay.att_status === "holiday")
              ? "H"
              : ""}
          </TableCell>
        );
      }
    }
    return rows;
  };

  // useEffect(() => {
  //   const existingIndex = downLoadJson.findIndex(
  //     (item) => item.user === monthlyDataJSON.user
  //   );
  //   if (existingIndex !== -1) {
  //     downLoadJson[existingIndex] = monthlyDataJSON;
  //   } else {
  //     downLoadJson.push(monthlyDataJSON);
  //   }
  // }, [month, year]);

  return (
    <>
      {open && (
        <>
          <TableCell
            sx={{
              border: "2px solid white",
              padding: 0,
              backgroundColor: "#edf6fc",
            }}
          />
          <TableCell
            sx={{
              border: "2px solid white",
              padding: 0,
              width: 80,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              fontSize: 12,
              backgroundColor: "#edf6fc",
            }}
          >
            {hq.tso_name}
          </TableCell>
          <TableCell
            align="center"
            sx={{
              border: "2px solid white",
              padding: 0,
              width: 80,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              fontSize: 12,
              backgroundColor: "#edf6fc",
            }}
          >
            {hq.hq_name}
          </TableCell>
          <TableCell
            style={{
              border: "2px solid white",
              padding: 0,

              width: 20,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              fontSize: 12,
              backgroundColor: "#edf6fc",
              textAlign: "center",
            }}
          >
            {hq.tso_status === "active" ? (
              <CheckCircleIcon style={{ color: "green", fontSize: "small" }} />
            ) : (
              <CancelIcon style={{ color: "red", fontSize: "small" }} />
            )}
          </TableCell>
          <TableCell
            align="center"
            style={{
              border: "2px solid white",
              padding: 0,
              cursor: "default",
              fontSize: 12,
              backgroundColor: "#edf6fc",
            }}
          >
            {total}
          </TableCell>
          {rowData()}
        </>
      )}
      {openModal && (
        <VisitDataModal
          open={openModal}
          setOpen={setOpenModal}
          day={day}
          userId={userId}
          month={month}
          year={year}
          name={name}
          customerTypeId={customerTypeId}
        />
      )}
      {openLeaveModal && (
        <LeaveDataModal
          open={openLeaveModal}
          setOpen={setOpenLeaveModal}
          day={day}
          month={month}
          year={year}
          name={name}
          leave={leave}
        />
      )}
    </>
  );
};

export default CalendarGridDailyVisitData;
